<template>
  <timeline-item
    :type="type"
    :title="title"
    :date="date"
    :icon="icon"
    color="is-calls"
  >
    <template v-slot:subtext>
      <p>
        {{ duration }}
        <span v-if="recordingUrlMessage" class="ml-50">{{
          recordingUrlMessage
        }}</span>
      </p>

      <p v-if="recordingURL">
        <template v-if="isVideoCall">
          <video width="320" height="240" controls>
            <source :src="recordingURL" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </template>
        <template v-else>
          <audio controls>
            <source :src="recordingURL" />
          </audio>
        </template>
      </p>

      <template v-else>
        <p v-if="call.recording_url">
          <button-link @click="getRecording" :loading="recordingLoading"
            >listen to
            {{ call.has_voicemail ? "voicemail" : "recording" }}</button-link
          >
        </p>
        <p v-else>
          <button-link
            v-if="!recordingUrlMessage"
            @click="getRecording"
            :loading="recordingLoading"
            >check for recording</button-link
          >
        </p>

        <p v-if="call.has_transcript">
          <button-link @click="getTranscript" :loading="transcriptLoading"
            >show transcript</button-link
          >
        </p>
      </template>

      <template v-if="isSmrtPhone">
        <template v-if="showCallId">
          <p>
            <button-link @click="showCallId = false">hide call sid</button-link>

            <button-link @click="copyCallSid(call.callrail_id)" size="is-small">
              {{ call.callrail_id }}
              <icon-default
                class="copy_icon ml-25"
                icon="copy"
                size="is-small"
              />
            </button-link>
          </p>
        </template>
        <template v-else>
          <p>
            <button-link @click="showCallId = true">show call sid</button-link>
          </p>
        </template>
      </template>

      <p v-if="createdBy">{{ createdBy }}</p>
    </template>

    <b-checkbox
      v-if="isInbound"
      size="is-small"
      @input="handleCheck($event, call.id)"
      type="is-success"
      :value="isAddressed"
      :disabled="isDisabled"
    >
      <template v-if="call.addressed_at">
        Addressed by <strong>{{ addressedByName }}</strong> on
        <strong>{{ addressedDate }}</strong>
      </template>
      <template v-else>Not addressed</template>
    </b-checkbox>

    <template v-slot:column>
      <slot name="column"></slot>
    </template>

    <modal-container>
      <modal-default
        ref="transcriptModal"
        :title="title"
        :subtitle="type"
        is-wide
        @close="closeTranscriptModal"
      >
        <section-basic title="Summary">
          <p>
            <template v-if="transcript.summary">{{
              transcript.summary
            }}</template>
            <template v-else>No Summary</template>
          </p>
        </section-basic>

        <section-basic title="Transcript">
          <div
            class="transcript_base"
            v-if="transcript.transcript"
            v-html="formattedTranscript"
          />
          <p v-else>No Transcript</p>
        </section-basic>

        <section-basic title="Call Details">
          <div
            class="transcript_base"
            v-if="transcript.call_details"
            v-html="formattedCallDetails"
          />
          <p v-else>No Details</p>
          <!--          <p-->
          <!--            v-for="(value, label) in transcript.smrtAgent.gathered_details"-->
          <!--            v-if="transcript.smrtAgent && transcript.smrtAgent.gathered_details"-->
          <!--          >-->
          <!--            <strong>{{ label }}:</strong> {{ value }}-->
          <!--          </p>-->
          <!--          <p v-else>No Details</p>-->
        </section-basic>
      </modal-default>
    </modal-container>
  </timeline-item>
</template>

<script>
import TimelineItem from "./TimelineItem";
import { ENUMS } from "../../services/enums";
import dates from "../../services/dates";
import utils from "../../services/utils";

export default {
  name: "TimelineCall",
  components: { TimelineItem },
  props: {
    call: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDisabled: false,
      recordingURL: null,
      recordingUrlMessage: null,
      recordingLoading: false,
      transcriptLoading: false,
      transcript: {},
      showCallId: false,
    };
  },
  computed: {
    isVideoCall() {
      return this.call.type === ENUMS.CommunicationTypeEnum.VideoCall.value;
    },
    isAddressed() {
      return !!this.call.addressed_at;
    },
    addressedByName() {
      return this.$store.getters.users.find(
        (user) => user.id === this.call.addressed_by
      ).full_name;
    },
    addressedDate() {
      return dates.formatAsDateTime(this.call.addressed_at);
    },
    isInbound() {
      return (
        this.call.communication_direction ===
        ENUMS.CommunicationDirectionEnum.Inbound.value
      );
    },
    type() {
      let suffix = " Call";

      if (this.isVideoCall) {
        suffix = " Video Call";
      }

      if (this.isInbound) {
        if (this.call.was_answered) {
          return "Inbound" + suffix;
        } else {
          return "Missed" + suffix;
        }
      } else {
        return "Outbound" + suffix;
      }
    },
    title() {
      const contact = utils.formatNumber(this.call.contact_number);

      const label = this.call.integration_label
        ? `${this.call.integration_label}: `
        : "";
      const number = label + utils.formatNumber(this.call.tracking_number);

      if (this.isInbound) {
        return contact + " > " + number;
      } else {
        return number + " > " + contact;
      }
    },
    date() {
      return dates.formatAsDateTime(this.call.created_at);
    },
    duration() {
      if (this.comm)
        return this.$utils.formatDuration(this.comm.duration_in_seconds);
    },
    createdBy() {
      if (!this.call.createdBy.display_name) return null;

      if (
        this.call.communication_direction ===
        ENUMS.CommunicationDirectionEnum.Outbound.value
      ) {
        return `by ${this.call.createdBy.display_name}`;
      }

      return null;
    },
    icon() {
      if (this.isInbound && !this.call.was_answered) {
        return "call-missed";
      } else {
        return "call";
      }
    },
    isSmrtPhone() {
      if (!this.$store.getters.isSmrtPhoneIntegrationComplete) return false;

      const phone = this.$store.getters.integrationPhoneNumbers.find(
        (phone) => phone.id === this.call.integration_phone_number_id
      );

      if (!phone) return false;

      return (
        this.call.callrail_id &&
        phone.integration_id === this.$store.getters.smrtPhoneIntegration.id
      );
    },
    formattedTranscript() {
      return utils.newLineString(this.transcript.transcript);
    },
    formattedCallDetails() {
      return utils.newLineString(this.transcript.call_details);
    },
  },
  methods: {
    getRecording() {
      this.recordingLoading = true;
      this.$forceUpdate();

      this.$api.communicationLogs.getRecording(this.call.id).then((res) => {
        if (res.data.url) {
          this.recordingURL = res.data.url;
        } else {
          this.recordingUrlMessage = "no recording";
        }

        this.recordingLoading = false;
        this.$forceUpdate();
      });
    },
    getTranscript() {
      this.transcriptLoading = true;
      this.$api.communicationLogs
        .getTranscript(this.call.id)
        .then((res) => {
          this.transcript = res.data;
          this.$refs.transcriptModal.open();
        })
        .finally(() => (this.transcriptLoading = false));
    },
    closeTranscriptModal() {
      this.transcript = {};
    },
    handleCheck(val, id) {
      const addressedBy = val ? this.$store.state.activeUser : null;

      this.isDisabled = true;

      this.$api.communicationLogs.setAddressed({ id, addressedBy }).then(() => {
        setTimeout(() => {
          this.isDisabled = false;
        }, 1000);
      });
    },
    copyCallSid(callSid) {
      utils.copyToClipboard(callSid);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.section_base {
  padding: 0 0 14px;
}

.section_base--inner p,
.transcript_base {
  line-height: 1.2;
  white-space: pre-line;
}
</style>
